import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  vaultLogin, vaultCheck, vaultLogout, vaultListDocuments,
  vaultUploadDocument, vaultDownloadDocument, vaultDeleteDocument, formatApiError,
} from "@/lib/api";
import { Seo } from "@/components/Seo";

const ALLOWED = ["pdf", "docx", "xlsx", "csv", "txt", "png", "jpg", "jpeg", "webp"];
const MAX_BATCH = 10;
const MAX_BYTES = 25 * 1024 * 1024;

const fmtDate = (iso) => {
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso || "—"; }
};
const fmtSize = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const inputCls =
  "w-full bg-transparent border border-[rgba(180,139,63,0.35)] px-4 py-3 text-[var(--bone)] font-serif2 text-lg placeholder:text-[var(--muted)] focus:outline-none focus:border-[var(--gold)] transition-colors";

function LoginView({ onAuthed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await vaultLogin(email.trim(), password);
      onAuthed();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Sign-in failed."));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="min-h-screen glow-navy flex items-center justify-center px-6">
      <form onSubmit={submit} className="w-full max-w-sm" data-testid="intake-login-form">
        <p className="text-center font-display uppercase tracking-luxe text-[10px] text-[var(--gold)]/80">Jennae Michelle Holdings</p>
        <h1 className="mt-3 text-center font-serif2 text-3xl text-[var(--bone)]">Business Records Vault</h1>
        <p className="mt-3 text-center text-sm text-[var(--muted)]">Hardened session. Authorized access only.</p>
        <div className="mt-10 flex flex-col gap-4">
          <input className={inputCls} type="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="intake-login-email" />
          <input className={inputCls} type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="intake-login-password" />
          <button disabled={loading} className="jmh-cta mt-2 disabled:opacity-50" data-testid="intake-login-submit">
            {loading ? "Signing in…" : "Enter"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function SecureIntake() {
  const [authed, setAuthed] = useState(null);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState([]);
  const [note, setNote] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    vaultCheck().then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  const load = async () => {
    setLoading(true);
    try { setDocs(await vaultListDocuments()); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  useEffect(() => { if (authed) load(); }, [authed]);

  const onSelect = (e) => {
    const valid = [];
    for (const f of Array.from(e.target.files || [])) {
      const ext = (f.name.split(".").pop() || "").toLowerCase();
      if (!ALLOWED.includes(ext)) { toast.error(`${f.name}: unsupported file type.`); continue; }
      if (f.size > MAX_BYTES) { toast.error(`${f.name}: exceeds the 25 MB limit.`); continue; }
      valid.push(f);
    }
    if (valid.length > MAX_BATCH) toast.error(`Up to ${MAX_BATCH} files at a time. Keeping the first ${MAX_BATCH}.`);
    setPending(valid.slice(0, MAX_BATCH));
    e.target.value = "";
  };

  const upload = async () => {
    if (!pending.length) return;
    setUploading(true);
    const batchId = (window.crypto && window.crypto.randomUUID) ? window.crypto.randomUUID() : `b_${Date.now()}`;
    let passed = 0;
    let quarantined = 0;
    let awaitingScan = 0;
    const failed = [];
    try {
      for (const f of pending) {
        try {
          const result = await vaultUploadDocument(f, note, batchId);
          if (result.malware_state === "SCAN_PASSED") passed += 1;
          else if (result.malware_state === "QUARANTINED") quarantined += 1;
          else awaitingScan += 1;
        } catch (e) { failed.push(f); toast.error(formatApiError(e.response?.data?.detail, `${f.name}: upload failed.`)); }
      }
      if (passed > 0) toast.success(`${passed} document${passed > 1 ? "s" : ""} stored. Malware scan passed.`);
      if (quarantined > 0) toast.warning(`${quarantined} document${quarantined > 1 ? "s" : ""} quarantined. Download is blocked.`);
      if (awaitingScan > 0) toast.warning(`${awaitingScan} document${awaitingScan > 1 ? "s" : ""} awaiting a passed scan. Download is blocked.`);
      setPending(failed); if (!failed.length) setNote(""); await load();
    } finally { setUploading(false); }
  };

  const open = async (d) => {
    setBusyId(d.file_id);
    try {
      const blob = await vaultDownloadDocument(d.file_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = d.original_filename || "document";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not download document."));
    } finally { setBusyId(null); }
  };

  const remove = async (d) => {
    if (!window.confirm(`Remove "${d.original_filename}" from the vault?`)) return;
    setBusyId(d.file_id);
    try {
      await vaultDeleteDocument(d.file_id);
      setDocs((p) => p.filter((x) => x.file_id !== d.file_id));
      toast.success("Document removed.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Could not remove document."));
    } finally { setBusyId(null); }
  };

  const doLogout = async () => { await vaultLogout(); setAuthed(false); };

  if (authed === null) return <div className="min-h-screen glow-navy" />;
  if (!authed) return <LoginView onAuthed={() => setAuthed(true)} />;

  return (
    <div className="min-h-screen glow-navy px-6 py-12">
      <Seo title="JMH — Business Records Vault" />
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-display uppercase tracking-luxe text-[10px] text-[var(--gold)]/80">Jennae Michelle Holdings</p>
            <h1 className="mt-1 font-serif2 text-3xl text-[var(--bone)]">Business Records Vault</h1>
          </div>
          <button onClick={doLogout} className="font-display uppercase tracking-wide2 text-xs text-[var(--gold-lite)]" data-testid="intake-logout">Sign out</button>
        </div>

        <p className="mt-4 text-sm text-[var(--muted)] leading-relaxed max-w-2xl">
          Private, custodian-managed records store. Every file is malware-scanned on receipt; only records that pass become downloadable. Up to {MAX_BATCH} files per upload; 25&nbsp;MB each.
        </p>
        <div className="mt-4 border border-[rgba(180,139,63,0.3)] bg-[rgba(180,139,63,0.05)] px-4 py-3 text-xs text-[var(--bone)]/70 max-w-2xl" data-testid="intake-scan-notice">
          <span className="font-display uppercase tracking-wide2 text-[10px] text-[var(--gold-lite)]">Hardened session · Malware-scanned</span>
          <span className="ml-2">Preview only. A malware scan passing does not verify authenticity, signatures, ownership or legal effect.</span>
        </div>

        <div className="corner-frame mt-8 px-6 md:px-8 py-8">
          <label className="block border border-dashed border-[rgba(180,139,63,0.35)] px-5 py-8 text-center cursor-pointer hover:border-[var(--gold)] transition-colors" data-testid="intake-drop">
            <input type="file" multiple accept=".pdf,.docx,.xlsx,.csv,.txt,.png,.jpg,.jpeg,.webp" className="hidden" onChange={onSelect} data-testid="intake-file-input" />
            <span className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold-lite)]">
              {pending.length ? `${pending.length} file(s) ready` : "Select documents to upload"}
            </span>
            {pending.length > 0 && <p className="mt-2 text-xs text-[var(--muted)]">{pending.map((f) => f.name).join(", ")}</p>}
          </label>
          <input className={`${inputCls} mt-4`} placeholder="Optional note (e.g. 'Articles of Organization')" value={note} onChange={(e) => setNote(e.target.value)} data-testid="intake-note" />
          <div className="mt-5 flex items-center gap-3">
            <button onClick={upload} disabled={!pending.length || uploading} className="jmh-cta disabled:opacity-40" data-testid="intake-upload-btn">
              {uploading ? "Uploading & scanning…" : `Upload ${pending.length || ""} to Vault`.trim()}
            </button>
            {pending.length > 0 && !uploading && (
              <button onClick={() => setPending([])} className="font-display uppercase tracking-wide2 text-xs text-[var(--muted)] hover:text-[var(--gold-lite)]" data-testid="intake-clear-btn">Clear</button>
            )}
          </div>
        </div>

        <div className="mt-10">
          <p className="font-display uppercase tracking-wide2 text-[11px] text-[var(--gold)]/80 mb-4">Stored Documents</p>
          {loading ? (
            <div className="text-[var(--muted)] py-16 text-center">Loading…</div>
          ) : docs.length === 0 ? (
            <div className="text-[var(--muted)] py-16 text-center" data-testid="intake-empty">No documents stored yet.</div>
          ) : (
            <ul className="space-y-3" data-testid="intake-list">
              {docs.map((d) => (
                <li key={d.file_id} className="border border-[rgba(180,139,63,0.22)] px-5 py-4 flex items-center justify-between gap-4" data-testid={`intake-item-${d.file_id}`}>
                  <div className="min-w-0">
                    <p className="text-[var(--bone)] font-serif2 text-lg truncate">{d.original_filename}</p>
                    <p className="text-xs text-[var(--muted)] truncate">{fmtSize(d.size)} · {fmtDate(d.created_at)}{d.note ? ` · ${d.note}` : ""}</p>
                    <span className="mt-1 inline-block font-display uppercase tracking-wide2 text-[9px] text-[var(--gold-lite)]/70" data-testid={`intake-scan-${d.file_id}`}>
                      {d.malware_label || "Pending malware scan"} · {d.sensitivity_class || "RESTRICTED_BUSINESS"}
                    </span>
                  </div>
                  <div className="shrink-0 flex items-center gap-4">
                    <button onClick={() => open(d)} disabled={busyId === d.file_id || d.malware_state !== "SCAN_PASSED"} title={d.malware_state !== "SCAN_PASSED" ? "Available after malware scan passes" : ""} className="font-display uppercase tracking-wide2 text-[10px] text-[var(--gold-lite)] hover:text-[var(--gold)] disabled:opacity-40" data-testid={`intake-open-${d.file_id}`}>Download</button>
                    <button onClick={() => remove(d)} disabled={busyId === d.file_id} className="font-display uppercase tracking-wide2 text-[10px] text-[var(--muted)] hover:text-red-400 disabled:opacity-40" data-testid={`intake-delete-${d.file_id}`}>Remove</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
